import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";

const ApplicationSuccess = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/applications/${applicationId}`);
      setApplication(response.data);
    } catch (error) {
      console.error("Failed to fetch application", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Application not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex items-center justify-center p-6">
      <Card className="max-w-2xl w-full shadow-xl bg-white">
        <CardHeader className="text-center space-y-4 bg-[#F7B538]/10 border-b border-[#F7B538]">
          <div className="w-20 h-20 bg-[#F7B538] rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-12 h-12 text-[#0E366A]" />
          </div>
          <CardTitle className="text-3xl font-bold text-[#0E366A]">Application Submitted Successfully!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-[#F7B538]/20 p-6 rounded-lg border-2 border-[#F7B538]">
            <p className="text-center text-lg font-semibold text-[#0E366A] mb-2">Your Application Code</p>
            <p 
              data-testid="application-code"
              className="text-center text-3xl font-bold text-[#0E366A]"
            >
              {application.application_code}
            </p>
            <p className="text-center text-sm text-gray-700 mt-2">Please save this code for future reference</p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-[#0E366A]">Application Date:</span>{" "}
              {(() => {
                const date = new Date(application.created_at);
                // Convert to EST and format as YYYY-MM-DD HH:MM
                const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
                const year = estDate.getFullYear();
                const month = String(estDate.getMonth() + 1).padStart(2, '0');
                const day = String(estDate.getDate()).padStart(2, '0');
                const hours = String(estDate.getHours()).padStart(2, '0');
                const minutes = String(estDate.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}`;
              })()}
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-[#0E366A] text-lg">Waitlist Positions:</h3>
            {application.students.map((student, idx) => (
              <div key={idx} className="bg-[#F7B538]/10 p-4 rounded-lg border border-[#F7B538]">
                <p className="font-semibold text-[#0E366A]">
                  {student.first_name} {student.last_name}
                </p>
                <p className="text-sm text-gray-700">
                  Grade: {student.grade_applying} | Position: #{application.waitlist_positions[student.grade_applying]}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#0E366A]/5 p-4 rounded-lg border border-[#0E366A]/20">
            <p className="text-sm text-gray-700 leading-relaxed">
              You will receive a confirmation email with your application details and waitlist position. 
              We will notify you as your position moves forward in the waitlist.
            </p>
          </div>

          <div className="flex gap-4">
            <Button 
              data-testid="back-to-application-btn"
              onClick={() => navigate('/application')}
              className="flex-1 bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Back to Applications
            </Button>
            <Button 
              data-testid="back-to-home-btn"
              onClick={() => navigate('/')}
              variant="outline"
              className="flex-1 border-[#0E366A] text-[#0E366A] hover:bg-[#F7B538]/20"
            >
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicationSuccess;